import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { hasPermission, getUserDashboard, getUserPortal } from './permissions';
import { styles } from './RouteGuards.styles';

/**
 * AuthGuard — Pure authentication check.
 * Renders <Outlet /> if user is authenticated, redirects to /login if not.
 * No role or permission checks happen here.
 */
export const AuthGuard: React.FC = () => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    const token = localStorage.getItem('auth_token');
    if (!token) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (isLoading) {
        return (
            <div style={styles.loadingScreen}>
                Validating Auth Session...
            </div>
        );
    }

    if (!isAuthenticated || !user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <Outlet />;
};

/**
 * PermissionGuard — Checks a single required permission.
 * If the user lacks the permission, redirects to their role-appropriate dashboard.
 * Does NOT check roles or path prefixes — permissions are the sole gatekeeper.
 */
interface PermissionGuardProps {
    requiredPermission?: string;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({ requiredPermission }) => {
    const { user } = useAuth();

    if (requiredPermission && !hasPermission(user, requiredPermission)) {
        return <Navigate to={getUserDashboard(user)} replace />;
    }

    return <Outlet />;
};

/**
 * RoleLayoutGuard — Ensures the user's role matches allowedRoles[].
 * Used to wrap role-specific portal sections (superadmin, agent, student, consultancy).
 * If role doesn't match, redirects to the user's own dashboard.
 */
interface RoleLayoutGuardProps {
    allowedRoles: string[];
}

export const RoleLayoutGuard: React.FC<RoleLayoutGuardProps> = ({ allowedRoles }) => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    const token = localStorage.getItem('auth_token');
    if (!token) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (isLoading) {
        return (
            <div style={styles.loadingScreen}>
                Validating Access...
            </div>
        );
    }

    if (!isAuthenticated || !user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // allowedRoles are portals: superadmin | consultancy | agent | student
    if (!allowedRoles.includes(getUserPortal(user))) {
        return <Navigate to={getUserDashboard(user)} replace />;
    }

    return <Outlet />;
};

/**
 * AdaptiveLayoutGuard — Renders the correct layout shell based on the user's role
 * WITHOUT restricting which routes can be accessed.
 * Used for shared routes like /workspace/* that any role can access if they have permission.
 */
export const getLayoutForRole = (user: Parameters<typeof getUserPortal>[0]): string => getUserPortal(user);


// ─── Legacy exports for backward compatibility during migration ─────────
// These are thin wrappers so existing route imports don't break during
// the transition. They all delegate to the new guards above.

export const ProtectedRoute = PermissionGuard;
export const SuperAdminRoute: React.FC = () => <RoleLayoutGuard allowedRoles={['superadmin']} />;
export const AgentRoute: React.FC = () => <RoleLayoutGuard allowedRoles={['agent']} />;
export const StudentRoute: React.FC = () => <RoleLayoutGuard allowedRoles={['student']} />;
